import { useState } from 'react';
import PrimaryButton from '../button/PrimaryButton.tsx';

interface RedemptionCardProps {
  onSubmit: (e: React.FormEvent<HTMLFormElement>) => Promise<void>;
  initialCode?: string;
}

interface FormErrors {
  voucherCode?: string;
  pin?: string;
  captcha?: string;
}

function RedemptionCard({ onSubmit, initialCode = '' }: RedemptionCardProps): JSX.Element {
  const [voucherCode, setVoucherCode] = useState(initialCode);
  const [pin, setPin] = useState('');
  const [errors, setErrors] = useState<FormErrors>({});

  // Validation function
  const validateForm = (): boolean => {
    let formErrors: FormErrors = {};
    let isValid = true;

    if (!voucherCode) {
      formErrors.voucherCode = "Voucher code cannot be empty.";
      isValid = false;
    }

    if (!pin) {
      formErrors.pin = "Pin cannot be empty.";
      isValid = false;
    } else {
      if (!/^\d{4}$/.test(pin)) {
        formErrors.pin = "Voucher pin must be a four-digit number.";
        isValid = false;
      }
    }

    setErrors(formErrors);
    return isValid;
  };

  // Handle form submission
  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();

    if (validateForm()) {
      try {
        await onSubmit(e);
      } catch (error) {
        // Error is handled by parent component
        // No need to log here
      }
    }
  };

  return (
    <div className="bg-white rounded-2xl shadow-2xl md:p-8 p-6 w-full max-w-lg border border-[#B0B0B0]">
      {/* Header */}
      <div className="mb-8 flex items-center justify-start">

        <div className="inline-flex items-center justify-center w-16 h-16 bg-bgGreen rounded-full ">
          <img src="https://saraluat.blr1.cdn.digitaloceanspaces.com/redemption_portal/voucher%20icon.png" className='w-10 sm:h-10' alt="" />
        </div>



        <div className="ml-5 sm:ml-5 flex-1 min-w-0">
          <h2 className="[font-family:'Inter',Helvetica] font-semibold text-[#1b312e] md:text-[26px] text-[20px] ">Avail Your Voucher</h2>
          <p className="[font-family:'Inter',Helvetica] font-medium text-[#666666] text-[17px]">Enter your voucher details to proceed</p>
        </div>

      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Voucher Code Field */}
        <div>
          <label htmlFor="code" className=" font-medium text-[#1b312e] text-lg tracking-[0]">
            Voucher Code
          </label>
          <input
            type="text"
            id="code"
            name="code"
            placeholder="e.g. DHJ7389JSH2991HY"
            value={voucherCode}
            onChange={(e) => {
              let value = e.target.value.toUpperCase().replace(/[^A-Z0-9-]/g, "");
              setVoucherCode(value);
              if (errors.voucherCode) {
                setErrors(prev => ({ ...prev, voucherCode: undefined }));
              }
            }}
            className="mt-1 w-full px-4 py-3 border-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#53978e]/70 focus:border-transparent transition-all duration-200 border-gray-200"
          />
          {errors.voucherCode && (
            <p className="text-red-500 text-sm mt-2 flex items-center">
              <span className="w-1 h-1 bg-red-500 rounded-full mr-2"></span>
              {errors.voucherCode}
            </p>
          )}
        </div>

        {/* PIN Field */}
        <div>
          <label htmlFor="pin" className="font-medium text-[#1b312e] text-lg  whitespace-nowrap">
            Security PIN
          </label>
          <input
            type="password"
            id="pin"
            name="pin"
            placeholder="Enter 4-digit PIN"
            value={pin}
            onChange={(e) => {
              setPin(e.target.value);
              if (errors.pin) {
                setErrors(prev => ({ ...prev, pin: undefined }));
              }
            }}
            className="mt-1 w-full px-4 py-3 border-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#53978e]/70 focus:border-transparent transition-all duration-200 border-gray-200"
          />
          {errors.pin && (
            <p className="text-red-500 text-sm mt-2 flex items-center">
              <span className="w-1 h-1 bg-red-500 rounded-full mr-2"></span>
              {errors.pin}
            </p>
          )}
        </div>

        {/* Submit Button */}
        <div className="pt-4">
          <PrimaryButton type="submit" className="w-full py-4 text-lg font-semibold ">
            Get Your Voucher
          </PrimaryButton>
        </div>
      </form>

      {/* Security Notice */}
      <div className="mt-6 p-4 bg-bgGreen rounded-lg border border-green-100">
        <div className="flex flex-col  items-start">
          <div className='flex items-center'>
            <img
              className='h-[15px] mr-2'
              alt="Vector"
              src="https://saraluat.blr1.cdn.digitaloceanspaces.com/redemption_portal/secure%20logo.png"
            />
            <div className='text-textGreen font-medium'>Secure Transaction</div>
          </div>
          <p className="ml-5 font-medium   text-[#666666] text-sm  ">Your voucher information is encrypted and secure</p>
        </div>
      </div>
    </div>

  );
}

export default RedemptionCard; 